import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PersonalDetailsForm from './PersonalDetailsForm';
import CompanyInfoForm from './CompanyInfoForm';
import SystemSelectionForm from './SystemSelectionForm';
import PeopleMetricsForm from './PeopleMetricsForm';
import MetricsSelectionForm from './MetricSelectionForm';
import { completeOnboarding, updateUser, updateOrganisation, useOrganisationsByOwner, createOrganisation } from '../../util/db';
import { useAuth } from '../../util/auth';
import '../../styles/OnboardingSteps.css'; // Importing the new CSS file
import { SparklesIcon } from '@heroicons/react/24/outline';
import supabase from '../../util/supabase';

export default function OnboardingSteps() {
  const [currentStep, setCurrentStep] = useState(0);
  const history = useHistory();
  const auth = useAuth();
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedSystems, setSelectedSystems] = useState({
    ats: '',
    hris: ''
  });
  const { data: organisations } = useOrganisationsByOwner(auth?.user?.uid);

  const handleMetricsSelection = (selected) => {
    setSelectedMetrics(selected);
  };

  const handleSystemsSelection = async (systems) => {
    try {
      console.log('Attempting to save systems:', systems);
      
      if (!auth?.user?.uid) {
        throw new Error('User ID not found');
      }

      const existingOrg = organisations?.[0];
      let response;

      try {
        if (existingOrg) {
          console.log('Updating existing organisation:', existingOrg.id);
          response = await updateOrganisation(existingOrg.id, {
            ats: systems.ats,
            hris: systems.hris
          });
        } else {
          console.log('Creating new organisation');
          response = await createOrganisation({
            owner: auth.user.uid,
            ats: systems.ats,
            hris: systems.hris
          });
        }

        console.log('Organisation operation response:', response);
        
        if (!response) {
          throw new Error('No data returned from database operation');
        }

        console.log('Systems saved successfully:', response);
        handleNextStep();
        
      } catch (dbError) {
        console.error('Database operation failed:', dbError);
        throw new Error(`Database operation failed: ${dbError.message}`);
      }
    } catch (error) {
      console.error('Failed to update systems. Error:', error);
      alert(`Failed to save system selections: ${error.message}`);
    }
  };

  const steps = [
    { id: 'Step 1', name: 'Your Details', status: currentStep === 0 ? 'current' : currentStep > 0 ? 'complete' : 'upcoming' },
    { id: 'Step 2', name: 'Company Info', status: currentStep === 1 ? 'current' : currentStep > 1 ? 'complete' : 'upcoming' },
    { id: 'Step 3', name: 'Your Systems', status: currentStep === 2 ? 'current' : currentStep > 2 ? 'complete' : 'upcoming' },
    { id: 'Step 4', name: 'Choose Your Metrics', status: currentStep === 3 ? 'current' : currentStep > 3 ? 'complete' : 'upcoming' },
    { id: 'Step 5', name: 'Unlock your dashboard', status: currentStep === 4 ? 'current' : 'upcoming' },
  ];
  

  const handleNextStep = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        if (!auth?.user?.id) {
          throw new Error('User ID not available for onboarding completion');
        }

        // Wait for onboarding completion
        await completeOnboarding(auth.user.id);
        
        // Force refresh auth user data to get updated onboardingCompletedAt
        await auth.refreshUser();
        
        // Now navigate
        history.push('/dashboard');
        
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
        // Show error to user
        alert('Failed to complete onboarding. Please try again.');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepNavigation = (index) => {
    if (index <= currentStep) {
      setCurrentStep(index);
    }
  };

  const renderCurrentForm = () => {
    switch (currentStep) {
      case 0:
        return <PersonalDetailsForm onNextStep={handleNextStep} />;
      case 1:
        return <CompanyInfoForm onNextStep={handleNextStep} onBack={handleBack} />;
      case 2:
        return <SystemSelectionForm onSubmit={handleSystemsSelection} onBack={handleBack} />;
      case 3:
        return <MetricsSelectionForm onNextStep={handleNextStep} onMetricsSelect={handleMetricsSelection} onBack={handleBack} />;
      case 4:
        return <PeopleMetricsForm onNextStep={handleNextStep} selectedMetrics={selectedMetrics} onBack={handleBack} />;
      default:
        return <div className="message-error">Error: Invalid step</div>;
    }
  };
  

  const calculateProgressWidth = () => {
    const stepPercentage = 100 / steps.length;
    return `${stepPercentage * (currentStep + 1)}%`;
  };

  return (
    <div className="onboarding-container">
      <nav className="max-w-3xl mx-auto mb-12">
        <ol className="flex items-center justify-between relative">
          {/* Base line */}
          <div 
            className="absolute top-4 h-1 bg-gray-100 w-full rounded-full -z-10"
            aria-hidden="true"
          />
          {/* Gradient progress line */}
          <div 
            className="absolute top-4 h-1 rounded-full transition-all duration-500 -z-5"
            style={{ 
              width: `${(currentStep / (steps.length - 1)) * 100}%`,
              background: 'linear-gradient(to right, #667eea, #764ba2, #6B8DD6, #8E37D7)'
            }}
            aria-hidden="true"
          />
          
          {steps.map((step, index) => (
            <li 
              key={step.id}
              className="flex flex-col items-center"
            >
              <button
                onClick={() => handleStepNavigation(index)}
                disabled={index > currentStep}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center 
                  text-sm font-semibold transition-all duration-300 
                  transform hover:scale-110
                  ${index <= currentStep 
                    ? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-lg' 
                    : 'bg-white border-2 border-gray-200 text-gray-400'
                  }
                `}
              >
                {index === 4 ? ( // Last step
                  <SparklesIcon 
                    className={`h-4 w-4 ${index <= currentStep ? 'animate-pulse' : ''}`} 
                  />
                ) : (
                  index + 1
                )}
              </button>
              <span className={`
                mt-2 text-xs font-medium transition-all duration-300
                ${index <= currentStep 
                  ? 'text-[#667eea]' 
                  : 'text-gray-400'
                }
              `}>
                {step.name}
              </span>
              {/* Add pulse effect for current step */}
              {index === currentStep && (
                <div className="absolute top-4 -z-1">
                  <span className="animate-ping absolute inline-flex h-8 w-8 rounded-full bg-[#667eea] opacity-20"></span>
                </div>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <div className="form-container">
        {renderCurrentForm()}
      </div>
    </div>
  );
}

